import { action, makeAutoObservable, observable } from 'mobx';
import PublicAccRepo, { RemotePublicAccRepo } from '@repository/publicAcc/PublicAccRepo';
import request from '@stores/decorators/request';
import { AccResultRecordVO } from '@domain/vo/acca/v1/test/result/AccResultVO';
import { AccApplyResultVO } from '@domain/vo/acca/v1/test/result/apply/AccApplyResultVO';
import { AccApplyTipVO } from '@domain/vo/acca/v1/test/result/apply/AccApplyTipVO';
import { AccGameResultVO } from '@domain/vo/acca/v1/test/result/apply/AccGameResultVO';
import { AccSelfReportResultVO } from '@domain/vo/acca/v1/test/result/apply/AccSelfReportResultVO';
import { AccVideoDetailResultVO, AccVideoResultVO } from '@domain/vo/acca/v1/test/result/apply/AccVideoResultVO';
import { AccCompanyCurationVO } from '@domain/vo/acca/v1/test/result/competence/AccCompanyCurationVO';
import { AccJobCurationVO } from '@domain/vo/acca/v1/test/result/competence/AccJobCurationVO';
import { AccProfileResultVO } from '@domain/vo/acca/v1/test/result/competence/AccProfileResultVO';
import { PublicAccApplyUserStatusVO } from '@domain/vo/acca/v1/test/result/PublicAccApplyUserStatusVO';
import { VideoType } from '@domain/constant/acca/test/result/videoType';
import UrlUtil from '@utils/urlUtil';

export default class AccaPublicTestResultStore {
  private readonly repo: PublicAccRepo;

  /** 신역검 걸과 **/
  @observable code = UrlUtil.getParamFromUrl().code || '';
  @observable userName: string | null = null;
  @observable accResultSn: number | null = 0;
  @observable accResult: AccResultRecordVO | null = new AccResultRecordVO({
    resultType: 'VERY_GOOD',
    analysisType: 'COMPLETE',
    sn: 0,
    code: '',
    name: '',
    externalYn: false,
    applicationDate: '',
    version: 'V2',
  }); // 역검 결과
  @observable publicAccApplyUserStatus: PublicAccApplyUserStatusVO | null = null;

  /** 신역검 응시 분석 결과 **/
  @observable accApplyResult: AccApplyResultVO | null = null;
  @observable accSelfReportResult: AccSelfReportResultVO | null = null;
  @observable accVideoResult: AccVideoResultVO | null = null;
  @observable accVideoDetailMap: Map<VideoType, AccVideoDetailResultVO[]> | null = null;
  @observable accGameResult: AccGameResultVO | null = null;
  @observable accApplyTip: AccApplyTipVO | null = null;

  /** 신역검 역량 분석 결과 **/
  @observable accProfileResult: AccProfileResultVO | null = null;
  @observable accCompanyCuration: AccCompanyCurationVO | null = null;
  @observable accJobCuration: AccJobCurationVO | null = null;

  constructor() {
    this.repo = new RemotePublicAccRepo();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  @action.bound
  setAccResultSn = (accResultSn: number | null) => {
    this.accResultSn = accResultSn;
  };

  /** 커리어센터 결과표 정보 **/
  @action.bound
  @request
  async fetchAccStatusUser() {
    const { data } = await this.repo.fetchAccStatusUser(this.code);
    this.publicAccApplyUserStatus = new PublicAccApplyUserStatusVO(data);

    //분석 상태, 버전 정보에 대해서 업데이트 시킴
    this.accResult = new AccResultRecordVO({
      resultType: 'VERY_GOOD',
      analysisType: data.analysisType,
      sn: 0,
      code: '',
      name: data.name,
      externalYn: false,
      applicationDate: '',
      version: data.version,
    }); // 역검 결과
    this.userName = data.name;
    return data;
  }

  /** 신역검 응시 분석 결과 **/
  @action.bound
  @request
  async fetchAccApplyResult() {
    const { data } = await this.repo.fetchAccApplyResult(this.code);
    this.accApplyResult = data;
  }

  @action.bound
  @request
  async fetchAccApplySelfReport() {
    const { data } = await this.repo.fetchAccApplySelfReport(this.code);
    this.accSelfReportResult = data;
  }

  @action.bound
  @request
  async fetchAccApplyVideo() {
    const { data } = await this.repo.fetchAccApplyVideo(this.code);
    this.accVideoResult = data;

    this.accVideoDetailMap = new Map<VideoType, AccVideoDetailResultVO[]>();
    this.accVideoResult.details?.forEach((detail) => {
      const { type } = detail;
      this.accVideoDetailMap?.set(type, [...(this.accVideoDetailMap?.get(type) || []), detail]);
    });
  }

  @action.bound
  @request
  async fetchAccApplyGame() {
    const { data } = await this.repo.fetchAccApplyGame(this.code);
    this.accGameResult = data;
  }

  @action.bound
  @request
  async fetchAccApplyTip() {
    const { data } = await this.repo.fetchAccApplyTip(this.code);
    this.accApplyTip = data;
  }

  /** 신역검 역량 분석 결과 **/
  @action.bound
  @request
  async fetchAccCompetenceProfile() {
    const { data } = await this.repo.fetchAccCompetenceProfile(this.code);
    this.accProfileResult = data;
  }

  @action.bound
  @request
  async fetchAccCompetenceCurationCompany() {
    const { data } = await this.repo.fetchAccCompetenceCurationCompany(this.code);
    this.accCompanyCuration = data;
  }

  @action.bound
  @request
  async fetchAccCompetenceCurationJob() {
    const { data } = await this.repo.fetchAccCompetenceCurationJob(this.code);
    this.accJobCuration = data;
  }
}
